<script setup lang="ts">
import { DeleteConfirmationModal } from '@/components/common/DeleteConfirmationModal'
import RlEmptyDataset from '@/components/common/RlEmptyDataset.vue'
import RcContainersSelectList from '@/components/rc/common/RcContainersSelectList.vue'
import ContainerCard from '@/components/rc/work-order-dashboard/ContainerCard.vue'

import InvoiceCard from '@/components/rc/work-order-dashboard/InvoiceCard.vue'
import InvoiceCardHeader from '@/components/rc/work-order-dashboard/InvoiceCardHeader.vue'
import InvoiceCardRWOTable from '@/components/rc/work-order-dashboard/InvoiceCardRWOTable.vue'
import InvoiceCardSummary from '@/components/rc/work-order-dashboard/InvoiceCardSummary.vue'

import { useInvoices } from '@/composables/rc/invoices'
import type { IInvoiceContainer } from '@/types/rc/invoices'
import { getInvoiceCSV } from '@/services/rc.service'
import { formatCurrency } from '@/util/formatters'
import { OrderBy } from '@/types/shared/filters'
import { DEFAULT_PAGE_SIZES, DEFAULT_PAGINATION_LAYOUT } from '@/types/shared/pagination'
import type { IContainerDashboard } from '@/types/rc/containers'
import type { IInvoiceFilters } from '@/types/rc/filters'
import { Icon } from '@iconify/vue'
import { reactive, ref } from 'vue'
import { downloadFile } from '@/util'
import { useWorkOrder } from '@/composables/rc/work-order'
import { NotifyFail } from '@/helpers/core/notifications'

const props = defineProps<{ number: string }>()

const activeItems = ref<number[]>([])
const isCreatingInvoice = ref<boolean>(false)

const filters = reactive<IInvoiceFilters>({
  page: 1,
  per_page: DEFAULT_PAGE_SIZES[0],
  order_by: 'id',
  order_by_order: OrderBy.DESC,
  customer_id: undefined,
  status: undefined,
  work_order_number: props.number,
})

const containersSelected = ref<IContainerDashboard[]>([])

const containersListRef = ref<InstanceType<typeof RcContainersSelectList>>()
const resetContainersSelection = () => {
  containersSelected.value = []
  containersListRef.value?.clearSelection()
}

const { isManageableByCurrentUser } = useWorkOrder(props.number)

const { getInvoicesQuery, deleteInvoiceMutation, createInvoiceMutation, refreshInvoiceMutation } = useInvoices()

const { isFetching, isError, isRefetching, data, error, refetch } = getInvoicesQuery(filters)

const handleExport = async (invoiceID: number) => {
  try {
    const result = await getInvoiceCSV(invoiceID)
    downloadFile(result, `invoice_${invoiceID}.csv`)
  } catch (err: unknown) {
    const errMsg = err instanceof Error ? err.message : 'Unexpected error'
    NotifyFail(`There was an error downloading the CSV file. ${errMsg}.`)
  }
}

const handleDelete = async (invoiceID: number) => {
  DeleteConfirmationModal(
    'Delete Invoice',
    'Are you sure you want to delete this invoice? This action cannot be undone',
    invoiceID,
    deleteInvoiceMutation.mutateAsync
  )
}

const handleRefresh = async (invoiceID: number) => {
  DeleteConfirmationModal(
    'Refresh Invoice',
    'Are you sure you want to refresh this invoice? This action will generate it again and reset any previous status',
    invoiceID,
    refreshInvoiceMutation.mutateAsync,
    undefined,
    'Refresh'
  )
}

const showInvoiceCreationModal = ref(false)

const handleShowModal = async () => {
  showInvoiceCreationModal.value = true
}

const handleCreateInvoice = () => {
  isCreatingInvoice.value = true

  createInvoiceMutation.mutate(
    {
      work_order_number: props.number,
      container_ids: containersSelected.value.map((container) => container.id),
    },
    {
      onSuccess: () => {
        containersSelected.value = []
        showInvoiceCreationModal.value = false
      },
      onSettled: () => {
        isCreatingInvoice.value = false
      },
    }
  )
}

const containerSelected = ref<IInvoiceContainer | null>(null)
</script>

<template>
  <rl-error-result v-if="isError && !isRefetching" :error="error">
    <el-button @click="refetch()">
      <Icon icon="material-symbols:refresh-rounded" height="16" width="16" :inline="true" />&nbsp;Reload
    </el-button>
  </rl-error-result>
  <template v-else>
    <div v-loading="isFetching" class="flex flex-col gap-spacing-xl border-none">
      <div class="flex justify-end gap-spacing-md">
        <el-button v-if="isManageableByCurrentUser" type="primary" @click="handleShowModal">Create Invoice</el-button>
      </div>
      <template v-if="data?.data.length ?? 0">
        <!-- Container card to visualize a container's breakdown once clicked -->
        <ContainerCard v-if="containerSelected" :container="containerSelected" @back="containerSelected = null" />

        <!-- Collection of collapsible invoices -->
        <el-collapse v-if="!containerSelected" v-model="activeItems" class="flex flex-col gap-spacing-md border-none">
          <InvoiceCard
            v-for="invoice in data?.data"
            :key="invoice.id"
            :expanded="activeItems.includes(invoice.id)"
            :invoice="invoice"
          >
            <template #header>
              <InvoiceCardHeader
                :invoice="invoice"
                :expanded="activeItems.includes(invoice.id)"
                :editable="isManageableByCurrentUser"
                @export="handleExport"
                @delete="handleDelete"
                @refresh="handleRefresh"
              >
                <!-- Invoice total -->
                <h4 class="whitespace-nowrap text-sm">
                  <span class="text-text-tertiary">Total:&nbsp; </span> ${{ formatCurrency(invoice.total) }}
                </h4>

                <!-- Containers # -->
                <h4 class="whitespace-nowrap text-sm">
                  {{ invoice.total_containers }}
                  <span class="text-text-tertiary">
                    &nbsp; {{ invoice.total_containers == 1 ? 'container' : 'containers' }}
                  </span>
                </h4>

                <!-- Miners # -->
                <h4 class="whitespace-nowrap text-sm">
                  {{ invoice.total_miners }}
                  <span class="text-text-tertiary">&nbsp; {{ invoice.total_miners == 1 ? 'miner' : 'miners' }}</span>
                </h4>
              </InvoiceCardHeader>
            </template>

            <!-- Card's body - Has access to invoiceDetails after expanded -->
            <template #body="{ invoiceDetails }">
              <!-- Containers table -->
              <InvoiceCardRWOTable
                :containers="invoiceDetails?.containers"
                @row-click="(container: IInvoiceContainer) => (containerSelected = container)"
              />

              <el-divider v-if="invoiceDetails?.summary.length" class="invisible" />

              <!-- Summary table (common) -->
              <InvoiceCardSummary v-if="invoiceDetails?.summary.length" :summary="invoiceDetails?.summary" />
            </template>
          </InvoiceCard>
        </el-collapse>
      </template>
      <template v-else>
        <rl-empty-dataset title="Oops, nothing in here" subtitle="No data was returned for your search" />
      </template>
      <el-pagination
        v-if="data && data.total_pages > 0"
        v-model:current-page="filters.page"
        v-model:page-size="filters.per_page"
        class="self-end"
        :page-sizes="DEFAULT_PAGE_SIZES"
        :page-count="data.total_pages"
        :total="data.total_records"
        :disabled="isFetching"
        :layout="DEFAULT_PAGINATION_LAYOUT"
        background
      />
    </div>

    <!-- Create invoice dialog -->
    <el-dialog
      v-model="showInvoiceCreationModal"
      class="create-invoice"
      title="Create Invoice"
      width="544"
      @close="resetContainersSelection"
    >
      <p class="mb">Select containers to generate an invoice</p>
      <el-text class="color-stone-400">Containers</el-text>
      <RcContainersSelectList ref="containersListRef" v-model="containersSelected" :work-order-number="props.number" />
      <p class="mt-6">You are generating an invoice for {{ containersSelected.length }} containers</p>
      <template #footer>
        <el-button size="large" @click="showInvoiceCreationModal = false">Cancel</el-button>
        <el-button
          :loading="isCreatingInvoice"
          size="large"
          type="primary"
          :disabled="!containersSelected.length"
          @click="handleCreateInvoice"
        >
          Generate
        </el-button>
      </template>
    </el-dialog>
  </template>
</template>
